/**
 * Promo-code Excel/CSV import helpers (F8440).
 *
 * Spreadsheet contract:
 * - 1 data row = 1 variant/code; rows sharing the same Name become one article.
 * - Category = article category UUID.
 * - Stock type = article | variant | none (homogeneous within a Name group).
 * - Dynamic columns `Stock <POS name>` (empty or `-` = skip that POS).
 *
 * Internal JS uses camelCase; article create payloads keep Kore snake_case fields.
 * Column headers come from `articles.promo_codes_import.columns.*` in locale YAML.
 */
import * as XLSX from '@e965/xlsx';
import i18n from '../locales/i18n';
import Utils from './Utils';
import { fitWorksheetColumns } from './excelExport';
import {
    annotateHeaderComments,
    normalizeHeaderLabel as sharedNormalizeHeaderLabel,
    stripHeaderHints as sharedStripHeaderHints,
} from './importSpreadsheetHeaders';

/** @type {string} Article type created by this import. */
export const PROMO_CODE_ARTICLE_TYPE = 'code_discount';
export const DISCOUNT_TARGET_CART = 'cart';
export const DISCOUNT_TARGET_LAST_ITEM = 'last_item';

/** Internal fixed-column keys (camelCase) ↔ i18n suffix under `columns.*`. */
const FIXED_COLUMN_I18N_KEYS = {
    name: 'name',
    code: 'code',
    percentage: 'percentage',
    fixed: 'fixed',
    applyToAllCartItems: 'apply_to_all_cart_items',
    applyToLastItem: 'apply_to_last_item',
    category: 'category',
    stockType: 'stock_type',
};

/** @type {ReadonlyArray<keyof typeof FIXED_COLUMN_I18N_KEYS>} */
export const PROMO_CODE_FIXED_COLUMN_KEYS = Object.freeze(Object.keys(FIXED_COLUMN_I18N_KEYS));

const COLUMN_LABEL_LANGS = ['en', 'fr', 'de', 'it'];
const COLUMNS_I18N_PREFIX = 'articles.promo_codes_import.columns.';

/** Allowed values for the Stock type column. */
export const STOCK_TYPES = ['article', 'variant', 'none'];
export const STOCK_TYPE_VALUES_HINT = `(${STOCK_TYPES.join('|')})`;

const normalizeColumnLang = lang => {
    const short = String(lang || i18n.language || 'en').split(/[-_]/)[0].toLowerCase();
    return COLUMN_LABEL_LANGS.includes(short) ? short : 'en';
};

/**
 * Localized column labels used in the sample file and accepted on upload
 * (hints in parentheses are stripped when matching headers).
 *
 * @param {string} [lang] BCP-47 or short code (e.g. `fr-CH` → `fr`).
 * @returns {{
 *   name: string,
 *   code: string,
 *   percentage: string,
 *   fixed: string,
 *   applyToAllCartItems: string,
 *   applyToLastItem: string,
 *   category: string,
 *   stockType: string,
 *   stockPrefix: string,
 *   sheetName: string,
 * }}
 */
export const getPromoCodeColumnLabels = lang => {
    const short = normalizeColumnLang(lang);
    const t = i18n.getFixedT(short, 'translations');
    const hint = STOCK_TYPE_VALUES_HINT;

    return {
        name: t(`${COLUMNS_I18N_PREFIX}name`),
        code: t(`${COLUMNS_I18N_PREFIX}code`),
        percentage: t(`${COLUMNS_I18N_PREFIX}percentage`),
        fixed: t(`${COLUMNS_I18N_PREFIX}fixed`),
        applyToAllCartItems: t(`${COLUMNS_I18N_PREFIX}apply_to_all_cart_items`),
        applyToLastItem: t(`${COLUMNS_I18N_PREFIX}apply_to_last_item`),
        category: t(`${COLUMNS_I18N_PREFIX}category`),
        stockType: t(`${COLUMNS_I18N_PREFIX}stock_type`, { hint }),
        stockPrefix: t(`${COLUMNS_I18N_PREFIX}stock_prefix`),
        sheetName: t(`${COLUMNS_I18N_PREFIX}sheet_name`),
    };
};

/**
 * Display label for a fixed column in the current UI language (validation messages).
 * @param {keyof typeof FIXED_COLUMN_I18N_KEYS} key
 * @param {string} [lang]
 * @returns {string}
 */
export const getPromoCodeFixedColumnLabel = (key, lang) => {
    const labels = getPromoCodeColumnLabels(lang);
    return labels[key] || key;
};

export const STOCK_COLUMN_PREFIX = 'Stock ';
/** Same spreadsheet set as tickets import (SheetJS). */
export const FILE_EXTENSIONS = ['csv', 'xls', 'xlsx', 'ods', 'numbers'];

const sanitizeCell = value => {
    if (value === null || typeof value === 'undefined')
        return '';

    return String(value).replace(/[\u00A0\u202F]/g, ' ').trim();
};

/** Collapse whitespace/punctuation so "Stock type" and "stock_type" match. */
export const normalizeHeaderLabel = sharedNormalizeHeaderLabel;

/** Remove parenthetical hints such as `(0|1)` or `(article|variant|none)`. */
const stripHeaderHints = sharedStripHeaderHints;

/**
 * Normalize a salepoint display name for column matching (accents ignored).
 * @param {string} label
 * @returns {string}
 */
export const normalizeSalepointLabel = label =>
    sanitizeCell(label)
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .toLowerCase()
        .replace(/[\s._-]+/g, '');

/** Map normalized header text → fixed column key (`name`, `stockType`, …). Built lazily from i18n. */
let headerAliasesCache = null;

const getHeaderAliases = () => {
    if (headerAliasesCache)
        return headerAliasesCache;

    const aliases = {};

    const register = (key, label) => {
        if (!label)
            return;
        aliases[normalizeHeaderLabel(label)] = key;
        aliases[normalizeHeaderLabel(stripHeaderHints(label))] = key;
    };

    PROMO_CODE_FIXED_COLUMN_KEYS.forEach(key => {
        register(key, key);
        COLUMN_LABEL_LANGS.forEach(lang => {
            register(key, getPromoCodeColumnLabels(lang)[key]);
        });
    });

    headerAliasesCache = aliases;
    return aliases;
};

/** Localized "Stock " prefixes used to detect dynamic POS stock columns. */
let stockPrefixesCache = null;

const getStockPrefixes = () => {
    if (stockPrefixesCache)
        return stockPrefixesCache;

    stockPrefixesCache = [...new Set([
        STOCK_COLUMN_PREFIX.trim(),
        ...COLUMN_LABEL_LANGS.map(lang => sanitizeCell(getPromoCodeColumnLabels(lang).stockPrefix)),
    ])].filter(Boolean);

    return stockPrefixesCache;
};

const escapeRegExp = value => value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

/**
 * If `label` is a stock column (`Stock Lausanne`), return the POS name remainder.
 * @param {string} label
 * @returns {string|null}
 */
const matchStockColumnRemainder = label => {
    for (const prefix of getStockPrefixes()) {
        const match = label.match(new RegExp(`^${escapeRegExp(prefix)}\\s*(.+)$`, 'i'));
        if (match?.[1])
            return sanitizeCell(match[1]);
    }
    return null;
};

/**
 * @param {string} label
 * @returns {keyof typeof FIXED_COLUMN_I18N_KEYS|null}
 */
const resolveFixedHeaderKey = label => {
    const aliases = getHeaderAliases();
    const direct = aliases[normalizeHeaderLabel(label)];
    if (direct)
        return direct;

    return aliases[normalizeHeaderLabel(stripHeaderHints(label))] || null;
};

/** Truthy flags accepted in apply-to columns. */
const isTruthyFlag = value => {
    const normalized = sanitizeCell(value).toLowerCase();
    return ['1', 'true', 'yes', 'y', 'oui', 'x'].includes(normalized);
};

/**
 * @param {*} value
 * @returns {number} Parsed number, `0` if empty, or `NaN` if invalid.
 */
const parseNumber = value => {
    const sanitized = sanitizeCell(value).replace(',', '.');
    if (!sanitized.length)
        return 0;

    const parsed = Number(sanitized);
    return Number.isFinite(parsed) ? parsed : NaN;
};

/** Empty or `-` means "do not attach stock for this POS". */
const isSkippedStockCell = value => {
    const sanitized = sanitizeCell(value);
    return !sanitized.length || sanitized === '-';
};

/**
 * @param {{ name?: object, _id?: string }|null|undefined} salepoint
 * @param {string} lang
 * @returns {string}
 */
export const salepointDisplayName = (salepoint, lang) =>
    Utils.localized_or_fallback(salepoint?.name, lang) || salepoint?._id || '';

/** @type {string} AppConfig key for promo code input (online shop + POS magic search). */
export const PROMO_CODE_INPUT_SETTING_KEY = 'eshop.enable_promo_code';

/** Default when `eshop.enable_promo_code` is unset (matches engine default). */
export const PROMO_CODE_INPUT_DEFAULT_ENABLED = true;

/**
 * Whether a dotted settings path exists on an object (AppConfig `has_setting` semantics).
 *
 * @param {object|null|undefined} obj
 * @param {string} dotPath
 * @returns {boolean}
 */
export const hasSettingPath = (obj, dotPath) => {
    if (!obj)
        return false;

    let current = obj;
    for (const atom of dotPath.split('.')) {
        if (!current || typeof current !== 'object' || !Object.prototype.hasOwnProperty.call(current, atom))
            return false;
        current = current[atom];
    }
    return true;
};

/**
 * Effective `eshop.enable_promo_code` for a salepoint (global + optional override).
 *
 * @param {object|null|undefined} globalSetting
 * @param {object|null|undefined} salepoint
 * @returns {boolean}
 */
export const getEffectiveEnablePromoCode = (globalSetting, salepoint) => {
    const globalEnabled = globalSetting?.eshop?.enable_promo_code ?? PROMO_CODE_INPUT_DEFAULT_ENABLED;
    if (hasSettingPath(salepoint?.settings, PROMO_CODE_INPUT_SETTING_KEY))
        return !!salepoint.settings.eshop.enable_promo_code;
    return globalEnabled;
};

/**
 * Summarize global promo-code input and salepoints whose override differs.
 *
 * @param {object|null|undefined} globalSetting
 * @param {object[]} [salepoints]
 * @returns {{
 *   globalEnabled: boolean,
 *   enabledOverrides: object[],
 *   disabledOverrides: object[],
 * }}
 */
export const getPromoCodeInputStatus = (globalSetting, salepoints = []) => {
    const globalEnabled = globalSetting?.eshop?.enable_promo_code ?? PROMO_CODE_INPUT_DEFAULT_ENABLED;
    const enabledOverrides = [];
    const disabledOverrides = [];

    (salepoints || []).forEach(salepoint => {
        if (!hasSettingPath(salepoint?.settings, PROMO_CODE_INPUT_SETTING_KEY))
            return;

        const effective = !!salepoint.settings.eshop.enable_promo_code;
        if (effective === globalEnabled)
            return;

        if (effective)
            enabledOverrides.push(salepoint);
        else
            disabledOverrides.push(salepoint);
    });

    return { globalEnabled, enabledOverrides, disabledOverrides };
};

/** @type {RegExp} Matches the web/e-shop salepoint naming convention (see eshop `is_eshop`). */
const ESHOP_SALEPOINT_NAME = /eshop/i;

/**
 * @param {object|null|undefined} salepoint
 * @param {string} lang
 * @returns {boolean}
 */
export const isEshopSalepoint = (salepoint, lang) =>
    ESHOP_SALEPOINT_NAME.test(salepointDisplayName(salepoint, lang));

/**
 * @param {object|null|undefined} globalSetting
 * @returns {{ nativeEshop: boolean, wordpressSites: object[], hasWordPress: boolean }}
 */
export const getOnlineSalesChannels = globalSetting => {
    const nativeEshop = !!(globalSetting?.eshop?.uri?.trim());
    const wordpressSites = (globalSetting?.integrations?.wp?.websites || [])
        .filter(website => website?.uri?.trim());
    return {
        nativeEshop,
        wordpressSites,
        hasWordPress: wordpressSites.length > 0,
    };
};

/**
 * Native Ticketack e-shop uses global settings (no selected salepoint on public cart).
 *
 * @param {object|null|undefined} globalSetting
 * @returns {boolean}
 */
export const getNativeEshopPromoEnabled = globalSetting =>
    globalSetting?.eshop?.enable_promo_code ?? PROMO_CODE_INPUT_DEFAULT_ENABLED;

/**
 * WordPress merges the resolved salepoint settings into AppConfig.
 * Prefer the e-shop salepoint when present, else a unique edition salepoint.
 *
 * @param {object|null|undefined} globalSetting
 * @param {object[]} salepoints
 * @param {string} lang
 * @returns {boolean}
 */
export const getWordPressPromoEnabled = (globalSetting, salepoints = [], lang) => {
    const eshopSalepoints = salepoints.filter(salepoint => isEshopSalepoint(salepoint, lang));
    if (eshopSalepoints.length)
        return eshopSalepoints.some(salepoint => getEffectiveEnablePromoCode(globalSetting, salepoint));

    if (salepoints.length === 1)
        return getEffectiveEnablePromoCode(globalSetting, salepoints[0]);

    return getNativeEshopPromoEnabled(globalSetting);
};

/**
 * i18n keys (+ optional params) for promo-code input help lines under the import tab.
 *
 * @param {object|null|undefined} globalSetting
 * @param {object[]} salepoints
 * @param {string} lang
 * @returns {Array<{ key: string, params?: object }>}
 */
export const buildPromoCodeInputHelpItems = (globalSetting, salepoints = [], lang) => {
    const items = [];
    const status = getPromoCodeInputStatus(globalSetting, salepoints);
    const formatList = list => list.map(salepoint => salepointDisplayName(salepoint, lang)).join(', ');

    items.push({
        key: status.globalEnabled
            ? 'articles.promo_codes_import.promo_input_global_enabled'
            : 'articles.promo_codes_import.promo_input_global_disabled',
    });

    if (status.disabledOverrides.length) {
        items.push({
            key: 'articles.promo_codes_import.promo_input_disabled_at',
            params: { list: formatList(status.disabledOverrides) },
        });
    }

    if (status.enabledOverrides.length) {
        items.push({
            key: 'articles.promo_codes_import.promo_input_enabled_at',
            params: { list: formatList(status.enabledOverrides) },
        });
    }

    const { nativeEshop, hasWordPress } = getOnlineSalesChannels(globalSetting);
    const posSalepoints = salepoints.filter(salepoint => !isEshopSalepoint(salepoint, lang));
    const enabledPos = posSalepoints.filter(salepoint => getEffectiveEnablePromoCode(globalSetting, salepoint));

    if (nativeEshop && hasWordPress) {
        const eshopEnabled = getNativeEshopPromoEnabled(globalSetting);
        const wordpressEnabled = getWordPressPromoEnabled(globalSetting, salepoints, lang);
        if (eshopEnabled === wordpressEnabled) {
            items.push({
                key: eshopEnabled
                    ? 'articles.promo_codes_import.promo_input_online_both'
                    : 'articles.promo_codes_import.promo_input_online_both_disabled',
            });
        } else {
            items.push({
                key: eshopEnabled
                    ? 'articles.promo_codes_import.promo_input_online_eshop'
                    : 'articles.promo_codes_import.promo_input_online_eshop_disabled',
            });
            items.push({
                key: wordpressEnabled
                    ? 'articles.promo_codes_import.promo_input_online_wordpress'
                    : 'articles.promo_codes_import.promo_input_online_wordpress_disabled',
            });
        }
    } else {
        if (nativeEshop) {
            items.push({
                key: getNativeEshopPromoEnabled(globalSetting)
                    ? 'articles.promo_codes_import.promo_input_online_eshop'
                    : 'articles.promo_codes_import.promo_input_online_eshop_disabled',
            });
        }

        if (hasWordPress) {
            items.push({
                key: getWordPressPromoEnabled(globalSetting, salepoints, lang)
                    ? 'articles.promo_codes_import.promo_input_online_wordpress'
                    : 'articles.promo_codes_import.promo_input_online_wordpress_disabled',
            });
        }
    }

    if (posSalepoints.length) {
        if (enabledPos.length) {
            items.push({
                key: enabledPos.length === posSalepoints.length
                    ? 'articles.promo_codes_import.promo_input_pos_all'
                    : 'articles.promo_codes_import.promo_input_pos_at',
                params: enabledPos.length === posSalepoints.length
                    ? undefined
                    : { list: formatList(enabledPos) },
            });
        } else {
            items.push({ key: 'articles.promo_codes_import.promo_input_pos_disabled' });
        }
    }

    return items;
};

/**
 * Build a Map of normalized POS name → salepoint for stock column resolution.
 * @param {Array<{ _id: string, name?: object }>} salepoints
 * @param {string} lang
 * @returns {Map<string, object>}
 */
export const buildSalepointLookup = (salepoints, lang) => {
    const byNormalizedName = new Map();

    (salepoints || []).forEach(salepoint => {
        const label = salepointDisplayName(salepoint, lang);
        const key = normalizeSalepointLabel(label);
        if (key)
            byNormalizedName.set(key, salepoint);
    });

    return byNormalizedName;
};

/**
 * Localized header for a dynamic stock column, e.g. `Stock Lausanne`.
 * @param {object} salepoint
 * @param {string} lang
 * @returns {string}
 */
export const stockColumnHeader = (salepoint, lang) => {
    const labels = getPromoCodeColumnLabels(lang);
    return `${labels.stockPrefix}${salepointDisplayName(salepoint, lang)}`;
};

/**
 * Read the first sheet as a matrix of sanitized string cells (row 0 = headers).
 * @param {import('xlsx').WorkBook} workbook
 * @returns {string[][]}
 */
export const parseWorkbookRows = workbook => {
    const firstSheetName = workbook.SheetNames?.[0];
    if (!firstSheetName)
        throw new Error('articles.promo_codes_import.invalid_file_content');

    const firstSheet = workbook.Sheets[firstSheetName];
    const parsedRows = XLSX.utils.sheet_to_json(firstSheet, {
        header: 1,
        raw: false,
        defval: '',
        blankrows: false,
    });

    if (!parsedRows?.length)
        throw new Error('articles.promo_codes_import.empty_file');

    return parsedRows.map(row => (Array.isArray(row) ? row.map(sanitizeCell) : []));
};

/**
 * Resolve fixed columns and `Stock <POS>` columns from the header row.
 *
 * @param {string[]} headerRow
 * @param {Map<string, { _id: string }>} salepointLookup
 * @returns {{
 *   fixed: Record<string, number>,
 *   stocks: Array<{ index: number, salepointId: string, label: string }>,
 *   unknown: string[],
 * }}
 */
export const mapHeaderRow = (headerRow, salepointLookup) => {
    const mapping = {
        fixed: {},
        stocks: [],
        unknown: [],
    };

    (headerRow || []).forEach((header, index) => {
        const label = sanitizeCell(header);
        if (!label.length)
            return;

        const fixedKey = resolveFixedHeaderKey(label);
        if (fixedKey) {
            mapping.fixed[fixedKey] = index;
            return;
        }

        const stockName = matchStockColumnRemainder(label);
        if (stockName) {
            const salepoint = salepointLookup.get(normalizeSalepointLabel(stockName));
            if (!salepoint) {
                mapping.unknown.push(label);
                return;
            }

            mapping.stocks.push({ index, salepointId: salepoint._id, label });
            return;
        }

        mapping.unknown.push(label);
    });

    return mapping;
};

const cellAt = (row, index) => {
    if (index === undefined || index === null)
        return '';
    return sanitizeCell(row?.[index]);
};

/**
 * Collect filled stock cells for a row (skips empty/`-`).
 * @param {string[]} row
 * @param {Array<{ index: number, salepointId: string }>} stockColumns
 * @returns {{ stocks: Array<{ salepointId: string, availability: number, raw: string }>, posIds: string[] }}
 */
const extractStocksFromRow = (row, stockColumns) => {
    const stocks = [];
    const posIds = [];

    stockColumns.forEach(({ index, salepointId }) => {
        const raw = cellAt(row, index);
        if (isSkippedStockCell(raw))
            return;

        const availability = parseNumber(raw);
        stocks.push({ salepointId, availability, raw });
        posIds.push(salepointId);
    });

    return { stocks, posIds };
};

/**
 * Parse data rows (everything after the header) into typed promo-code rows.
 * `line` is 1-based spreadsheet line number (header = 1).
 *
 * @param {string[][]} rows
 * @param {{ fixed: Record<string, number>, stocks: Array<{ index: number, salepointId: string }> }} headerMapping
 * @returns {Array<{
 *   line: number,
 *   name: string,
 *   code: string,
 *   percentage: number,
 *   percentageRaw: string,
 *   fixed: number,
 *   fixedRaw: string,
 *   stockType: string,
 *   category: string,
 *   applyAll: boolean,
 *   applyLast: boolean,
 *   stocks: Array<{ salepointId: string, availability: number, raw: string }>,
 *   posIds: string[],
 * }>}
 */
export const parsePromoCodeRows = (rows, headerMapping) => {
    const dataRows = rows.slice(1);
    const parsed = [];

    dataRows.forEach((row, offset) => {
        if (!row.some(cell => sanitizeCell(cell).length))
            return;

        const line = offset + 2;
        const name = cellAt(row, headerMapping.fixed.name);
        const code = cellAt(row, headerMapping.fixed.code);
        const percentageRaw = cellAt(row, headerMapping.fixed.percentage);
        const fixedRaw = cellAt(row, headerMapping.fixed.fixed);
        const stockType = sanitizeCell(cellAt(row, headerMapping.fixed.stockType)).toLowerCase();
        const category = cellAt(row, headerMapping.fixed.category);
        const applyAll = isTruthyFlag(cellAt(row, headerMapping.fixed.applyToAllCartItems));
        const applyLast = isTruthyFlag(cellAt(row, headerMapping.fixed.applyToLastItem));
        const { stocks, posIds } = extractStocksFromRow(row, headerMapping.stocks);

        parsed.push({
            line,
            name,
            code,
            percentage: parseNumber(percentageRaw),
            percentageRaw,
            fixed: parseNumber(fixedRaw),
            fixedRaw,
            stockType,
            category,
            applyAll,
            applyLast,
            stocks,
            posIds,
        });
    });

    return parsed;
};

/** Stable signature used to compare article-level stocks across grouped variants. */
const stocksSignature = stocks =>
    stocks
        .map(stock => `${stock.salepointId}:${stock.availability}`)
        .sort()
        .join('|');

/**
 * Group parsed rows by Name → one article per group, N variants.
 * @param {Array<{ name: string, line: number }>} rows
 * @returns {Array<{ name: string, rows: object[] }>}
 */
export const groupPromoCodeRows = rows => {
    const groups = new Map();

    rows.forEach(row => {
        const key = row.name || `__missing_name_${row.line}`;
        if (!groups.has(key))
            groups.set(key, []);
        groups.get(key).push(row);
    });

    return [...groups.entries()].map(([name, groupRows]) => ({ name, rows: groupRows }));
};

/**
 * Validate header mapping + grouped rows before create.
 * Error/warning `key` values are i18n paths under `articles.promo_codes_import.*`.
 *
 * @param {object} params
 * @param {{ fixed: Record<string, number>, unknown?: string[] }} params.headerMapping
 * @param {Array<{ name: string, rows: object[] }>} params.groups
 * @param {string[]} [params.categoryIds] Known article category UUIDs
 * @param {string[]} [params.existingCodes] Discount codes already in the instance
 * @returns {{ errors: Array<{ key: string, params?: object }>, warnings: Array<{ key: string, params?: object }>, valid: boolean }}
 */
export const validatePromoCodeImport = ({
    headerMapping,
    groups,
    categoryIds,
    existingCodes,
}) => {
    const errors = [];
    const warnings = [];
    const requiredFixed = ['name', 'code', 'category', 'stockType'];

    requiredFixed.forEach(key => {
        if (headerMapping.fixed[key] === undefined)
            errors.push({ key: 'articles.promo_codes_import.missing_column', params: { column: getPromoCodeFixedColumnLabel(key) } });
    });

    (headerMapping.unknown || []).forEach(column => {
        errors.push({ key: 'articles.promo_codes_import.unknown_column', params: { column } });
    });

    if (!groups.length)
        errors.push({ key: 'articles.promo_codes_import.no_rows' });

    const codesSeen = new Map();
    const categorySet = new Set(categoryIds || []);
    const existingSet = new Set([...(existingCodes || [])].map(code => sanitizeCell(code).toLowerCase()));

    groups.forEach(group => {
        const first = group.rows[0];
        const stockTypes = new Set(group.rows.map(row => row.stockType));
        const categories = new Set(group.rows.map(row => row.category));

        if (stockTypes.size > 1)
            errors.push({ key: 'articles.promo_codes_import.inconsistent_stock_type', params: { name: group.name } });

        if (categories.size > 1)
            errors.push({ key: 'articles.promo_codes_import.inconsistent_category', params: { name: group.name } });

        group.rows.forEach(row => {
            if (!row.name)
                errors.push({ key: 'articles.promo_codes_import.missing_name', params: { line: row.line } });

            if (!row.code)
                errors.push({ key: 'articles.promo_codes_import.missing_code', params: { line: row.line } });
            else {
                const codeKey = row.code.toLowerCase();
                if (codesSeen.has(codeKey)) {
                    errors.push({
                        key: 'articles.promo_codes_import.duplicate_code_in_file',
                        params: { code: row.code, line: row.line, otherLine: codesSeen.get(codeKey) },
                    });
                } else {
                    codesSeen.set(codeKey, row.line);
                }

                if (existingSet.has(codeKey))
                    errors.push({ key: 'articles.promo_codes_import.duplicate_code_existing', params: { code: row.code, line: row.line } });
            }

            if (!row.category)
                errors.push({ key: 'articles.promo_codes_import.missing_category', params: { line: row.line } });
            else if (categorySet.size && !categorySet.has(row.category))
                errors.push({ key: 'articles.promo_codes_import.unknown_category', params: { category: row.category, line: row.line } });

            if (!STOCK_TYPES.includes(row.stockType))
                errors.push({ key: 'articles.promo_codes_import.invalid_stock_type', params: { stockType: row.stockType || '∅', line: row.line } });

            if (row.applyAll && row.applyLast)
                errors.push({ key: 'articles.promo_codes_import.ambiguous_target', params: { line: row.line } });

            if (!row.applyAll && !row.applyLast)
                errors.push({ key: 'articles.promo_codes_import.missing_target', params: { line: row.line } });

            if (Number.isNaN(row.percentage))
                errors.push({ key: 'articles.promo_codes_import.invalid_percentage', params: { line: row.line, value: row.percentageRaw } });

            if (Number.isNaN(row.fixed))
                errors.push({ key: 'articles.promo_codes_import.invalid_fixed', params: { line: row.line, value: row.fixedRaw } });

            row.stocks.forEach(stock => {
                if (Number.isNaN(stock.availability) || stock.availability < 0) {
                    errors.push({
                        key: 'articles.promo_codes_import.invalid_stock_availability',
                        params: { line: row.line, value: stock.raw },
                    });
                }
            });

            if (row.stockType === 'none' && row.stocks.length)
                warnings.push({ key: 'articles.promo_codes_import.stocks_ignored_for_none', params: { line: row.line } });
        });

        // Article stock is shared: when several variants fill Stock columns, values must match.
        if (first?.stockType === 'article' && group.rows.length > 1) {
            const filled = group.rows.filter(row => row.stocks.length);
            if (filled.length > 1) {
                const signature = stocksSignature(filled[0].stocks);
                const inconsistent = filled.some(row => stocksSignature(row.stocks) !== signature);
                if (inconsistent) {
                    errors.push({
                        key: 'articles.promo_codes_import.inconsistent_article_stocks',
                        params: { name: group.name },
                    });
                }
            }
        }
    });

    return { errors, warnings, valid: errors.length === 0 };
};

const localizedName = (value, langs) => {
    const name = {};
    (langs || Utils.langs()).forEach(lang => {
        name[lang] = value;
    });
    return name;
};

const moneyBag = (amount, currency) => ({ [currency]: amount || 0 });

/**
 * Map internal stock rows to Kore article/variant stock entries (`salepoint_ids`).
 * @param {Array<{ salepointId: string, availability: number }>} stocks
 * @returns {Array<{ salepoint_ids: string[], availability: number }>}
 */
const toStockEntries = stocks =>
    stocks.map(stock => ({
        salepoint_ids: [stock.salepointId],
        availability: stock.availability,
    }));

/**
 * Build one article variant from a parsed row (Kore snake_case payload shape).
 * @param {object} row
 * @param {{ currency: string, langs: string[], vat: number, stockType: string }} options
 */
const buildVariant = (row, { currency, langs, vat, stockType }) => {
    const variant = {
        name: localizedName(row.code, langs),
        stock: 0,
        stocks: stockType === 'variant' ? toStockEntries(row.stocks) : [],
        stock_factor: 1,
        gtin: '',
        sku: '',
        vat,
        price: moneyBag(0, currency),
        value_like_price: true,
        value: moneyBag(0, currency),
        purchasing_price: moneyBag(0, currency),
        discount: {
            code: row.code,
            target: row.applyLast ? DISCOUNT_TARGET_LAST_ITEM : DISCOUNT_TARGET_CART,
            value: {
                percentage: row.percentage || 0,
                amount: moneyBag(row.fixed || 0, currency),
            },
            rules: {
                only_for_roles: Utils.defaultPricingSellerRoles(),
            },
        },
    };

    return variant;
};

/**
 * Build Kore `ArticlesApi.create` payloads from validated Name groups.
 * Sets `opaque.imported_at` so a batch can be identified after import.
 *
 * @param {Array<{ name: string, rows: object[] }>} groups
 * @param {object} [options]
 * @param {string} [options.currency]
 * @param {string[]} [options.langs]
 * @param {number} [options.vat]
 * @param {string} [options.importedAt] Timestamp `YYYYMMDDHHmmss`
 * @returns {object[]}
 */
export const buildArticlePayloads = (groups, {
    currency = Utils.currency(),
    langs = Utils.langs(),
    vat = Utils.tax()?.vat_articles ?? 0,
    importedAt,
} = {}) => {
    return groups.map(group => {
        const first = group.rows[0];
        const stockType = first.stockType;
        const variants = group.rows.map(row => buildVariant(row, { currency, langs, vat, stockType }));

        let stocks = [];
        let posIds = [];

        if (stockType === 'article') {
            const sourceRow = group.rows.find(row => row.stocks.length) || first;
            stocks = toStockEntries(sourceRow.stocks);
            posIds = sourceRow.posIds;
        } else if (stockType === 'variant') {
            posIds = [...new Set(group.rows.flatMap(row => row.posIds))];
        }

        return {
            type: PROMO_CODE_ARTICLE_TYPE,
            name: localizedName(group.name, langs),
            short_description: localizedName('', langs),
            description: localizedName('', langs),
            category: { _id: first.category },
            pos: posIds.map(id => ({ _id: id })),
            stock_type: stockType,
            stocks: stockType === 'article' ? stocks : [],
            supplier: '',
            posters: [],
            tags: [],
            attributes: [],
            delivery: {},
            opaque: {
                imported_at: importedAt,
            },
            variants,
        };
    });
};

/**
 * Sample workbook with localized headers and only the selected POS stock columns.
 *
 * @param {object} [options]
 * @param {object[]} [options.salepoints]
 * @param {string} [options.lang]
 * @param {string} [options.stockType]
 * @returns {import('xlsx').WorkBook}
 */
export const buildSampleWorkbook = ({ salepoints = [], lang, stockType = 'article' } = {}) => {
    const labels = getPromoCodeColumnLabels(lang);
    const headers = [
        labels.name,
        labels.code,
        labels.percentage,
        labels.fixed,
        labels.applyToAllCartItems,
        labels.applyToLastItem,
        labels.category,
        labels.stockType,
        ...salepoints.map(salepoint => stockColumnHeader(salepoint, lang)),
    ];

    const sampleCode = 'PROMO123';
    const sampleRow = [
        sampleCode,
        sampleCode,
        '0',
        '10',
        '1',
        '',
        '',
        stockType,
        ...salepoints.map(() => (stockType === 'none' ? '' : '1')),
    ];

    const sheet = XLSX.utils.aoa_to_sheet([headers, sampleRow]);
    fitWorksheetColumns(sheet);

    const fixedKeys = [...PROMO_CODE_FIXED_COLUMN_KEYS];
    annotateHeaderComments(sheet, fixedKeys, key => {
        const short = normalizeColumnLang(lang);
        const t = i18n.getFixedT(short, 'translations');
        const helpKey = `articles.promo_codes_import.column_help.${FIXED_COLUMN_I18N_KEYS[key]}`;
        const text = t(helpKey, { hint: STOCK_TYPE_VALUES_HINT });
        return text === helpKey ? '' : text;
    });

    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, sheet, labels.sheetName);
    return workbook;
};

/**
 * @param {File|Blob} file
 * @returns {Promise<string[][]>}
 */
export const readPromoCodesFile = async file => {
    const data = await file.arrayBuffer();
    const workbook = XLSX.read(data, { type: 'array', cellDates: true });
    return parseWorkbookRows(workbook);
};

/**
 * End-to-end parse → group → validate pipeline for an uploaded sheet.
 *
 * @param {object} params
 * @param {string[][]} params.rows
 * @param {object[]} params.salepoints
 * @param {string} params.lang
 * @param {string[]} [params.categoryIds]
 * @param {string[]} [params.existingCodes]
 * @returns {{
 *   headerMapping: object,
 *   parsedRows: object[],
 *   groups: Array<{ name: string, rows: object[] }>,
 *   validation: { errors: object[], warnings: object[], valid: boolean },
 * }}
 */
export const preparePromoCodesImport = ({
    rows,
    salepoints,
    lang,
    categoryIds,
    existingCodes,
}) => {
    const salepointLookup = buildSalepointLookup(salepoints, lang);
    const headerMapping = mapHeaderRow(rows[0] || [], salepointLookup);
    const parsedRows = parsePromoCodeRows(rows, headerMapping);
    const groups = groupPromoCodeRows(parsedRows);
    const validation = validatePromoCodeImport({
        headerMapping,
        groups,
        categoryIds,
        existingCodes,
    });

    return {
        headerMapping,
        parsedRows,
        groups,
        validation,
    };
};
